import { Injectable } from '@angular/core';
import { IMediaItem } from '@interfaces/media';
import { createHttpParams } from '@shared/functions';
import { IResponse } from '@interfaces/response';
import {
    IPlaylist, IPlaylistHot,
    IPlaylistMedia,
    IPlaylistMediaUpdated, IPlaylistMediaUpload, IPlaylistStripeUpdated,
    IPlaylistStripeUpload
} from '@interfaces/playlist';
import { HttpClient } from '@angular/common/http';
import { ErrorService } from '@services/error/error.service';
import { Observable, throwError } from 'rxjs';
import { catchError, share } from 'rxjs/operators';
import { IHotPlaylistDevice } from '@interfaces/devices';
import { IHotPlaylistGroup } from '@interfaces/group';

type PlaylistCategoryType = { hot: IPlaylistHot, main: IPlaylist };

@Injectable({
    providedIn: 'root'
})

export class PlaylistsService {
    constructor(
        private http: HttpClient,
        private errorService: ErrorService) {
    }


    // Preparing media for back-end
    public mapMediaForBackend(media: IMediaItem, chanel: string, sequence: number): IPlaylistMedia {
        const result = {
            chanel,
            sequence,
            duration: media.duration ? media.duration : 10
        } as any;
        if (!media.hasOwnProperty('playlistMediaId')) result.media = media.id;
        else result.playlistMediaId = media.playlistMediaId;

        return result;
    }

    // Factory for medias that will be send into back-end
    public createPlaylistMedias(medias: IMediaItem[]): IPlaylistMedia[] {
        const result: IPlaylistMedia[] = [];
        const audio = this.sortAudio(medias);
        const video = this.sortVideo(medias);

        const filterNewMedia = (media: IMediaItem[], chanel: string) => {
            for (let i = 0; i < media.length; i++) {
                if (media[i].hasOwnProperty('playlistMediaId')) continue;
                result.push(this.mapMediaForBackend(media[i], chanel, i));
            }
        };

        filterNewMedia(audio, 'audio');
        filterNewMedia(video, 'visual');

        return result;
    }

    private sortVideo(medias: IMediaItem[]): IMediaItem[] {
        return medias.filter(media => {
            if (media.mediaType === 'rss' || media.mediaType === 'text' || media.mediaType === 'audio') {
                return;
            }
            return media;
        }).sort((a: any, b: any) => a.sequence - b.sequence);
    }

    private sortAudio(medias: IMediaItem[]): IMediaItem[] {
        return medias.filter(media => {
            if (media.mediaType !== 'audio') {
                return;
            }
            return media;
        }).sort((a: any, b: any) => a.sequence - b.sequence);
    }


    public fetchPlaylists<T extends keyof PlaylistCategoryType>(
        {limit, skip = 0, order = '-created_at', search = '', type}:
        { limit: number, skip?: number, order?: string, search?: string, type: T }
    ): Observable<IResponse<PlaylistCategoryType[T]>> {
        const params = createHttpParams({
            limit,
            skip,
            order,
            filter: search,
            type
        });

        return this.http.get<IResponse<PlaylistCategoryType[T]>>('playlists', {params})
            .pipe(
                share(),
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public getPlaylistById(id: number): Observable<IPlaylist | IPlaylistHot> {
        return this.http.get<IPlaylist | IPlaylistHot>(`playlists/${id}`);
    }

    public createPlaylist({name, description = '', medias, type = 'main', stripes}:
                          {
                              name: string,
                              description: string,
                              medias: IPlaylistMediaUpload[],
                              type?: string,
                              stripes: IPlaylistStripeUpload[]
                          }
    ): Observable<any> {
        return this.http.post('playlists', {
            name,
            description,
            medias,
            stripes,
            type
        }).pipe(
            catchError(err => {
                this.errorService.showError(err, false);
                return throwError(err);
            })
        );
    }

    public createHotPlaylist({name, description = '', hotEndLocal, hotStartLocal, hotRepetitionEndLocal, hotRepetition, medias, stripes}:
                             {
                                 name: string,
                                 description: string,
                                 medias: IPlaylistMediaUpload[],
                                 hotStartLocal: string,
                                 hotEndLocal: string,
                                 hotRepetition: string,
                                 hotRepetitionEndLocal: string,
                                 stripes: IPlaylistStripeUpload[]
                             }
    ): Observable<IPlaylistHot> {
        return this.http.post<IPlaylistHot>('playlists', {
            name,
            description,
            medias,
            stripes,
            hotStartLocal,
            hotEndLocal,
            hotRepetitionEndLocal,
            hotRepetition,
            type: 'hot'
        }).pipe(
            catchError(err => {
                this.errorService.showError(err, false);
                return throwError(err);
            })
        );
    }

    public updatePlaylist(
        id: number,
        playlist: {
            name?: string, description?: string,
            hotStartLocal?: string, hotEndLocal?: string, hotRepetitionEndLocal?: string, hotRepetition?: string,
            mediasToDelete?: number[],
            mediasToCreate?: IPlaylistMediaUpload[],
            mediasToUpdate?: IPlaylistMediaUpdated[],
            stripesToCreate?: IPlaylistStripeUpload[],
            stripesToDelete?: number[],
            stripesToUpdate?: IPlaylistStripeUpdated[]
        }): Observable<IPlaylist | IPlaylistHot> {
        return this.http.patch<IPlaylist | IPlaylistHot>(`playlists/${id}`, playlist)
            .pipe(
                share(),
                catchError(err => {
                    this.errorService.showError(err, false);
                    return throwError(err);
                })
            );
    }

    public deletePlaylist(id: number): Observable<any> {
        return this.http.delete(`playlists/${id}`)
            .pipe(
                share(),
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public fetchHotPlaylistDevices(skip: number, limit: number, hotPlaylistId: number, search?: string): Observable<IResponse<IHotPlaylistDevice>> {
        const params = createHttpParams({skip, limit, hotPlaylistId, order: 'id', onSchedule: 1, search: search || ''});
        return this.http.get<IResponse<IHotPlaylistDevice>>('devices/by-hot-playlist', {params})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err, false);
                    return throwError(err);
                })
            );
    }

    public fetchHotPlaylistGroups(skip: number, limit: number, hotPlaylistId: number, search?: string): Observable<IResponse<IHotPlaylistGroup>> {
        const params = createHttpParams({skip, limit, hotPlaylistId, search: search || ''});
        return this.http.get<IResponse<IHotPlaylistGroup>>('groups/by-hot-playlist', {params})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err, false);
                    return throwError(err);
                })
            );
    }
}
